'use client'

import { useState } from 'react'
import { useIntl } from 'react-intl'
import { InfoCircle16, X24 } from '@vinted/monochrome-icons'
import { Button, Cell, Dialog, Divider, Icon, Navigation, Spacer, Text } from '@vinted/web-ui'

import { formatCurrencyAmount } from '@marketplace-web/domain/currency-data'
import { ScrollableArea, SeparatedList } from '@marketplace-web/shared/ui-helpers'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { ComplaintEstimatedReleaseModel } from '@marketplace-web/domain/complaints-data'

type Props = {
  isOpen: boolean
  isBundleOrder: boolean
  refundEstimate: ComplaintEstimatedReleaseModel | null
  onClose: () => void
}

const TotalRefundModal = ({ isOpen, refundEstimate, onClose, isBundleOrder }: Props) => {
  const { locale } = useIntl()
  const translate = useTranslate('total_refund_modal')

  const discountsInfoBodyTranslationKey = isBundleOrder
    ? 'discounts_info'
    : 'single_item_discounts_info'

  const [showDiscountInfo, setShowDiscountInfo] = useState<boolean>(false)

  const handleModalClose = () => onClose()

  const renderItems = () => {
    if (!refundEstimate?.details?.items) return []

    return refundEstimate.details.items.map((item, index) => (
      <Cell
        styling={Cell.Styling.Tight}
        key={`${item.title}-${index}`}
        body={item.title}
        suffix={formatCurrencyAmount(item.original, locale)}
      />
    ))
  }

  const handleInfoClick = () => setShowDiscountInfo(true)

  const handleDiscountsInfoClose = () => setShowDiscountInfo(false)

  const renderTotalDiscount = () => {
    if (!refundEstimate?.details.totalItemsDiscount) return null

    return (
      <>
        <Spacer size={Spacer.Size.Large} />
        <Cell
          styling={Cell.Styling.Tight}
          body={
            <div className="u-flexbox">
              {translate('order_discount')}
              <Spacer orientation={Spacer.Orientation.Vertical} />
              <button
                className="u-flexbox u-align-items-center"
                onClick={handleInfoClick}
                type="button"
                aria-label={translate('a11y.discounts_info_icon')}
              >
                <Icon
                  name={InfoCircle16}
                  color={Icon.Color.GreyscaleLevel2}
                  testId="discounts-info-icon"
                />
              </button>
            </div>
          }
          suffix={formatCurrencyAmount(refundEstimate.details.totalItemsDiscount, locale, {
            formatAsNegative: true,
          })}
        />
      </>
    )
  }

  const renderShippingVat = () => {
    if (!refundEstimate?.details.refundableShippingVat) return null

    return (
      <>
        <Spacer size={Spacer.Size.Large} />
        <Cell
          styling={Cell.Styling.Tight}
          body={<Text as="span" text={translate('shipping_vat')} />}
          suffix={formatCurrencyAmount(refundEstimate.details.refundableShippingVat, locale)}
        />
      </>
    )
  }

  const renderTotalRefund = () => {
    if (!refundEstimate) return null

    return (
      <>
        <Spacer size={Spacer.Size.Large} />
        <Cell
          styling={Cell.Styling.Tight}
          prefix={<Text as="h2" type={Text.Type.Title} text={translate('total_refund')} />}
          suffix={
            <Text
              as="h2"
              type={Text.Type.Title}
              text={formatCurrencyAmount(refundEstimate.totalEstimatedRefund, locale)}
            />
          }
        />
      </>
    )
  }

  const renderContent = () => {
    if (showDiscountInfo)
      return (
        <Cell>
          <Text as="span" text={translate(discountsInfoBodyTranslationKey)} />
        </Cell>
      )

    if (!refundEstimate) return null

    return (
      <Cell>
        <Cell styling={Cell.Styling.Tight}>
          <SeparatedList separator={<Spacer size={Spacer.Size.Small} />}>
            {renderItems()}
          </SeparatedList>
        </Cell>
        {renderTotalDiscount()}
        {renderShippingVat()}
        {renderTotalRefund()}
      </Cell>
    )
  }

  if (!refundEstimate) return null

  return (
    <Dialog hasScrollableContent show={isOpen}>
      <div className="u-fill-width">
        <Navigation
          body={showDiscountInfo ? translate('discounts_title') : translate('total_refund')}
          right={
            <Button
              inline
              iconName={X24}
              styling={Button.Styling.Flat}
              testId="total-refund-modal-close"
              aria={{ 'aria-label': translate('a11y.close') }}
              onClick={showDiscountInfo ? handleDiscountsInfoClose : handleModalClose}
            />
          }
        />
        <Divider />
        <ScrollableArea>
          {renderContent()}
          <Cell>
            <Button
              text={translate('close')}
              styling={Button.Styling.Filled}
              onClick={showDiscountInfo ? handleDiscountsInfoClose : handleModalClose}
            />
          </Cell>
        </ScrollableArea>
      </div>
    </Dialog>
  )
}

export default TotalRefundModal
